import React from "react";
import { useNexOs } from "@/lib/nexOs";
import { useLabel } from "@/lib/dynamicEngine";

const DOT_CLASS = {
  success: "bg-emerald-400 shadow-[0_0_0_4px_rgba(34,197,94,0.15)]",
  info:    "bg-sky-400 shadow-[0_0_0_4px_rgba(56,189,248,0.15)]",
  warning: "bg-amber-400 shadow-[0_0_0_4px_rgba(251,191,36,0.18)]",
  danger:  "bg-red-500 shadow-[0_0_0_4px_rgba(239,68,68,0.18)]",
};

const EVENT_TONE = {
  success: "text-emerald-300",
  info:    "text-sky-200",
  warning: "text-amber-300",
  danger:  "text-red-300",
};

/**
 * Tier 1 (right rail) — Live System Feed.
 * Renders structured events: {time, event, details, status}.
 */
export default function LiveSystemFeed() {
  const { feed } = useNexOs();
  const heading = useLabel("nds.feed.heading", "LIVE SYSTEM FEED");
  const kicker = useLabel("nds.feed.kicker", "streaming · every 30s");

  return (
    <aside
      data-testid="nds-live-feed"
      className="nds-card-dark p-4 flex flex-col h-full"
    >
      <div className="flex items-center justify-between pb-3 border-b border-white/8">
        <div className="flex items-center gap-2">
          <span className="nds-live-dot" />
          <span className="font-mono text-[0.75rem] tracking-[0.24em] text-nex-silver uppercase">
            {heading}
          </span>
        </div>
        <span className="font-mono text-[0.65rem] text-nex-muted tracking-widest uppercase whitespace-nowrap">
          {kicker}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto max-h-[520px] mt-1 divide-y divide-white/5">
        {feed.map((ev, idx) => {
          const status = ev.status || "info";
          const time = ev.time || ev.ts || "";
          const eventLabel = ev.event || ev.message || "Event";
          const details = ev.details || (ev.event ? "" : ev.message) || "";
          return (
            <div
              key={ev.id || `${idx}-${eventLabel}`}
              className="py-3 px-1 flex gap-3"
              data-testid={`nds-feed-item-${idx}`}
            >
              <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${DOT_CLASS[status] || DOT_CLASS.info}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3">
                  <span className={`font-mono text-[0.7rem] font-semibold tracking-[0.14em] uppercase truncate ${EVENT_TONE[status] || EVENT_TONE.info}`}>
                    {eventLabel}
                  </span>
                  <span className="font-mono text-[0.6rem] text-nex-muted tracking-[0.18em] uppercase whitespace-nowrap">
                    {time}
                  </span>
                </div>
                {details && (
                  <div className="mt-1 text-[0.78rem] text-nex-silver/85 leading-snug">
                    {details}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {feed.length === 0 && (
          <div className="p-4 font-mono text-xs text-nex-chrome" data-testid="nds-feed-empty">
            No events yet.
          </div>
        )}
      </div>
    </aside>
  );
}
